import { useEffect, useRef } from "react";
import { pollStatus } from "./auth-actions";
import { AuthError } from "./auth-error";
import { AuthSession, loadAuthSession } from "./auth-session";

const BASE_INTERVAL_MS = 7000;
const MAX_INTERVAL_MS = 60000;

/**
 * Опрос /v3/auth/status, пока открыт экран ввода кода.
 * Запросы не перекрываются; при сетевой ошибке интервал растёт,
 * при 429 — пауза из Retry-After. При возврате в приложение — сразу запрос.
 */
export const useStatusPolling = ({
  enabled,
  onUpdate,
  onError,
}: {
  enabled: boolean;
  onUpdate: (session: AuthSession) => void;
  /** Вернуть false, чтобы остановить опрос (например, сессия закрыта). */
  onError: (error: AuthError) => boolean;
}) => {
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  onUpdateRef.current = onUpdate;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let stopped = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = BASE_INTERVAL_MS;

    const schedule = (ms: number) => {
      clearTimeout(timer);
      if (!stopped) {
        timer = setTimeout(tick, ms);
      }
    };

    const tick = async () => {
      if (stopped || inFlight) {
        return;
      }

      const session = loadAuthSession();
      if (!session) {
        stopped = true;
        return;
      }

      inFlight = true;
      try {
        const next = await pollStatus(session);
        if (stopped) return;
        delay = BASE_INTERVAL_MS;
        onUpdateRef.current(next);
        schedule(delay);
      } catch (error) {
        if (stopped) return;
        const authError = error as AuthError;

        if (!onErrorRef.current(authError)) {
          stopped = true;
          return;
        }

        if (authError.code === "NETWORK") {
          delay = Math.min(delay * 2, MAX_INTERVAL_MS);
          schedule(delay);
        } else if (authError.retryAfterMs !== undefined) {
          schedule(Math.max(authError.retryAfterMs, BASE_INTERVAL_MS));
        } else {
          schedule(delay);
        }
      } finally {
        inFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        schedule(0);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    schedule(0);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled]);
};
